import React, { forwardRef, useMemo, SVGAttributes } from 'react';
import { generateShortUid } from '../../utils/id';
import type { SVGIconInput, SVGIconData } from '../../types';

interface SVGIconProps extends SVGAttributes<SVGSVGElement> {
    icon: SVGIconInput;
    title?: string;
    className?: string;
}

// Type guard: check if icon is SVGIconData (object from svg-sprite-loader)
function isSpriteIcon(icon: SVGIconInput): icon is SVGIconData {
    return typeof icon === 'object' && icon !== null && 'id' in icon && 'viewBox' in icon;
}

const SVGIcon = forwardRef<SVGSVGElement, SVGIconProps>(function SVGIconComponent(
    { icon, title = '', ...props },
    ref
) {
    const titleId = useMemo(() => generateShortUid(), []);

    // Handle React component (from @svgr/webpack)
    if (!isSpriteIcon(icon)) {
        const IconComponent = icon;
        return <IconComponent ref={ref} aria-label={title || undefined} {...props} />;
    }

    // Handle SVGIconData (from svg-sprite-loader)
    return (
        <svg ref={ref} viewBox={icon.viewBox} aria-labelledby={title ? titleId : undefined} {...props}>
            {title ? <title id={titleId}>{title}</title> : null}
            <use xlinkHref={`#${icon.id}`} href={`#${icon.id}`} />
        </svg>
    );
});

SVGIcon.displayName = 'SVGIcon';

export default SVGIcon;
